import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, MessageSquare, FileText, Smartphone, Megaphone,
  Users, GitBranch, Code2, CreditCard, Settings, Shield, Building2, MessageCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCompany } from "@/contexts/CompanyContext";

const tenantNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/channels", label: "Channels", icon: Smartphone },
  { to: "/broadcasts", label: "Broadcasts", icon: Megaphone },
  { to: "/audience", label: "Audience", icon: Users },
  { to: "/cascades", label: "Cascades", icon: GitBranch },
  { to: "/integration", label: "Integration", icon: Code2 },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
];

const adminNav = [
  { to: "/admin", label: "Admin Dashboard", icon: Shield, exact: true },
  { to: "/admin/tenants", label: "Tenants", icon: Building2 },
];

export function AppSidebar() {
  const { pathname } = useLocation();
  const { company } = useCompany();

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  return (
    <aside className="hidden md:flex h-dvh w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/15 text-primary">
          <MessageCircle className="h-4 w-4" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-white">InferenceLabs</div>
          <div className="text-[11px] text-sidebar-muted">WhatsApp BSP</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
          Workspace
        </div>
        <ul className="space-y-0.5">
          {tenantNav.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
                  isActive(item.to, item.exact)
                    ? "bg-sidebar-accent text-white"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-white",
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-6 px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
          Platform Admin
        </div>
        <ul className="space-y-0.5">
          {adminNav.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
                  isActive(item.to, item.exact)
                    ? "bg-sidebar-accent text-white"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-white",
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <div className="rounded-md bg-sidebar-accent/40 px-3 py-2.5">
          <div className="text-[11px] text-sidebar-muted">Account balance</div>
          <div className="mt-0.5 flex items-baseline justify-between">
            <span className="text-sm font-semibold text-white">${company?.balance.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? "0.00"}</span>
            <Link to="/billing" className="text-[11px] font-medium text-primary hover:underline">
              Top up
            </Link>
          </div>
        </div>
      </div>
    </aside>
  );
}
